export interface ShareInvitationOptions {
  displayName?: string;
  jobTitle?: string;
  profileUrl: string;
}

/** Invitation copy without the URL (for LinkedIn, which adds a link preview). */
export function buildShareInvitationText(
  options: Omit<ShareInvitationOptions, 'profileUrl'> & { profileUrl?: string }
): string {
  const displayName = options.displayName?.trim() || 'I';
  const jobTitle = options.jobTitle?.trim();

  const intro = jobTitle
    ? `${displayName} (${jobTitle}) invited you to view their SkipScreen profile.`
    : `${displayName} invited you to view their SkipScreen profile.`;

  return [
    intro,
    '',
    'SkipScreen helps candidates showcase their skills beyond a resume — portfolio, video responses, and more.',
  ].join('\n');
}

export function buildShareInvitationMessage(options: ShareInvitationOptions): string {
  const profileUrl = options.profileUrl.trim();
  return `${buildShareInvitationText(options)}\n\nView the profile here: ${profileUrl}`;
}

export function linkedInShareUrl(profileUrl: string, message: string): string {
  const text = encodeURIComponent(message);
  const url = encodeURIComponent(profileUrl);
  return `https://www.linkedin.com/feed/?shareActive=true&text=${text}&url=${url}`;
}

export function whatsAppShareUrl(message: string): string {
  return `https://api.whatsapp.com/send?text=${encodeURIComponent(message)}`;
}

export function mailtoShareUrl(
  message: string,
  options?: { recruiterEmail?: string; candidateName?: string }
): string {
  const name = options?.candidateName?.trim();
  const subject = name
    ? `${name} shared their SkipScreen profile with you`
    : 'SkipScreen profile invitation';
  const query = [
    `subject=${encodeURIComponent(subject)}`,
    `body=${encodeURIComponent(message)}`,
  ].join('&');
  const email = options?.recruiterEmail?.trim();
  return email ? `mailto:${email}?${query}` : `mailto:?${query}`;
}

/** Open an external share URL in a new tab without navigating the current page. */
export function openShareWindow(url: string): void {
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.target = '_blank';
  anchor.rel = 'noopener noreferrer';
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
}
